import React, { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import Text from "@/components/ui/text";
import Badge from "@/components/ui/badge";
import { IconButton } from "@/components/ui/icon-button";
import IconContainer from "@/components/ui/icon-container";
import { Edit, InfoCircle } from "@dreamhub/designsystem/icons";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger
} from "@/components/ui/tooltip";
import { ContractData } from "@/pages/contracts/types";
import {
  getCommittedRevenue,
  resolveEffectiveMetric
} from "@/pages/contracts/committedRevenue";
import {
  useAccountCurrency,
  useCurrencyConversion
} from "@/services/hooks/useCurrencyConversion";
import { useArrMrr } from "@/services/context/ArrMrrContext";
import {
  getCurrencySymbol,
  monetaryValueFormatter
} from "@/pages/deals/helpers";
import { distinguishCurrencySign, reducePrecision } from "@/utils/numbers";
import FinancialRecognitionModal from "./components/FinancialRecognitionModal";
import { type ContractFieldUpdate } from "../ContractDetailedView";
import { FinancialRecognitionValues, MetricDisplay } from "./components/types";
import { isModified } from "./components/utils";

interface Props {
  contract: ContractData;
  onUpdateField: ContractFieldUpdate;
  editDisabled?: boolean;
  editDisabledTooltipText?: string;
  /**
   * Deals show no committed (CARR/CMRR) row: a deal's services are almost
   * always future-dated, so the backend already reports the committed value as
   * the deal's ARR and a separate CARR row would repeat it. Same prop name and
   * meaning as `ContractTermsV2`'s, which the deal page sets alongside this one.
   */
  showCommittedValues?: boolean;
}

const ContractFinancialRecognition: React.FC<Props> = ({
  contract,
  onUpdateField,
  editDisabled = false,
  editDisabledTooltipText,
  showCommittedValues = true
}) => {
  const { t } = useTranslation();
  const { isArr } = useArrMrr();
  const [isModalOpen, setIsModalOpen] = useState(false);

  const currency = contract.currency || "USD";
  const contractCurrencySymbol = getCurrencySymbol(currency);
  // The unresolved currency, not the display fallback, so the hook's own
  // unknown-currency guard suppresses a conversion rather than fabricating one
  // against a contract that has no currency — matching ContractTopFields.
  const { accountCurrencySymbol, showConversion } = useCurrencyConversion(
    contract.currency
  );
  // Read separately from useCurrencyConversion, which substitutes USD while the
  // setting is still loading — comparing against a guessed account currency
  // would qualify a contract in the account's own currency for one render and
  // then drop it again. Null until it genuinely resolves, which the guard in
  // distinguishCurrencySign reads as nothing to tell apart.
  const accountCurrency = useAccountCurrency();
  // Each row prints the contract's figure with the account's beside it, so a
  // contract currency whose symbol is the account's own glyph — `$` for CAD on
  // a USD account — leaves both halves reading as the same currency. Resolved
  // from the same getCurrencySymbol both sides already use, per the
  // same-resolver obligation distinguishCurrencySign documents.
  const displayCurrencySymbol =
    distinguishCurrencySign({
      sign: contractCurrencySymbol,
      // The raw currency: a contract with none recorded is unknown, not USD,
      // and the defaulted code qualified it to the literal "USD" on any
      // $-glyph account.
      currencyCode: contract.currency,
      accountSign: accountCurrencySymbol,
      accountCurrencyCode: accountCurrency
      // getCurrencySymbol answers undefined for a currency its package does not
      // carry, and monetaryValueFormatter interpolates — without this the panel
      // renders the literal text "undefined5,000".
    }) ?? "";

  const recurring: MetricDisplay = {
    label: isArr ? t("contractTerms.arr") : t("contractTerms.mrr"),
    recognizedLocal: isArr ? contract.arrLocal : contract.mrrLocal,
    recognizedAccount: isArr ? contract.arr : contract.mrr,
    calculatedLocal:
      (isArr ? contract.calculatedArrLocal : contract.calculatedMrrLocal) ??
      (isArr ? contract.arrLocal : contract.mrrLocal),
    calculatedAccount:
      (isArr ? contract.calculatedArr : contract.calculatedMrr) ??
      (isArr ? contract.arr : contract.mrr)
  };

  const committed: MetricDisplay = {
    label: isArr ? t("contractTerms.carr") : t("contractTerms.cmrr"),
    ...getCommittedRevenue(contract, isArr),
    tooltip: t("contractTerms.committedTooltip")
  };

  const acv: MetricDisplay = {
    label: t("contractTerms.acv"),
    recognizedLocal: contract.annualContractValueLocal,
    recognizedAccount: contract.annualContractValue,
    calculatedLocal:
      contract.calculatedAnnualContractValueLocal ??
      contract.annualContractValueLocal,
    calculatedAccount:
      contract.calculatedAnnualContractValue ?? contract.annualContractValue,
    tooltip: t("contractTerms.acvFull")
  };

  const tcv: MetricDisplay = {
    label: t("contractTerms.tcv"),
    recognizedLocal: contract.totalContractValueLocal,
    recognizedAccount: contract.totalContractValue,
    calculatedLocal:
      contract.calculatedTotalContractValueLocal ??
      contract.totalContractValueLocal,
    calculatedAccount:
      contract.calculatedTotalContractValue ?? contract.totalContractValue,
    tooltip: t("contractTerms.tcvFull")
  };

  const metrics = showCommittedValues
    ? [recurring, committed, acv, tcv]
    : [recurring, acv, tcv];

  const handleSave = useCallback(
    (values: FinancialRecognitionValues) => {
      if (editDisabled) return;
      const entries = Object.entries(values).filter(
        ([, v]) => v !== undefined
      ) as [string, string | number | null][];
      if (entries.length === 0) return;
      const [firstField, firstValue] = entries[0];
      const additional = Object.fromEntries(entries.slice(1));
      // Recognized values are explicit financial overrides: a 0 entered here
      // is a real value the user wants to freeze, so it must not collapse to
      // null the way a cleared inline CARR/CMRR edit does.
      onUpdateField(firstField, firstValue, additional, {
        preserveExplicitZero: true
      });
    },
    [onUpdateField, editDisabled]
  );

  return (
    <div className="flex flex-col gap-4 w-full">
      <div className="flex items-center justify-between">
        <Text variant="smSubtitle">
          {t("contracts.contractView.financialRecognition")}
        </Text>
        <IconButton
          size="lg"
          variant="icon"
          onClick={() => setIsModalOpen(true)}
          disabled={editDisabled}
          tooltipText={
            editDisabled && editDisabledTooltipText
              ? editDisabledTooltipText
              : t("contracts.contractView.financialRecognition")
          }
          data-test="financial-recognition-edit"
        >
          <Edit />
        </IconButton>
      </div>
      <div className="grid grid-cols-[auto,1fr] w-full">
        {metrics.map((metric) => {
          const { local: effectiveLocal, account: effectiveConverted } =
            resolveEffectiveMetric(metric);
          const displayValue =
            effectiveLocal != null
              ? monetaryValueFormatter(
                  reducePrecision(effectiveLocal, 0),
                  displayCurrencySymbol
                )
              : "—";
          const showConverted =
            showConversion &&
            effectiveLocal != null &&
            effectiveLocal > 0 &&
            effectiveConverted != null;
          const modified = isModified(
            metric.recognizedLocal,
            metric.calculatedLocal
          );
          return (
            <React.Fragment key={metric.label}>
              <div className="flex items-center gap-1 h-9 pr-4">
                <Text variant="bodySecondary">{metric.label}</Text>
                {metric.tooltip ? (
                  <Tooltip>
                    <TooltipTrigger>
                      <IconContainer color="secondary">
                        <InfoCircle />
                      </IconContainer>
                    </TooltipTrigger>
                    <TooltipContent maxWidth={18}>
                      {metric.tooltip}
                    </TooltipContent>
                  </Tooltip>
                ) : null}
                {modified ? (
                  <Badge
                    text={t("contracts.contractView.modified")}
                    textVariant="smBodySecondary"
                    className="py-[2px] px-2"
                  />
                ) : null}
              </div>
              <div className="flex items-center justify-end gap-2 h-9">
                <Text variant="body">{displayValue}</Text>
                {showConverted ? (
                  <Text variant="bodyTertiary">
                    (
                    {monetaryValueFormatter(
                      reducePrecision(effectiveConverted ?? 0, 0),
                      accountCurrencySymbol
                    )}
                    )
                  </Text>
                ) : null}
              </div>
            </React.Fragment>
          );
        })}
      </div>
      {contract.financialNote ? (
        <div className="flex flex-col gap-1 pt-2 border-t border-border-border1">
          <Text variant="bodySecondary">
            {t("contracts.contractView.notes")}
          </Text>
          <Text variant="body" className="whitespace-pre-wrap">
            {contract.financialNote}
          </Text>
        </div>
      ) : null}
      <FinancialRecognitionModal
        open={isModalOpen}
        setIsOpen={setIsModalOpen}
        contract={contract}
        onSave={handleSave}
        showCommittedValues={showCommittedValues}
      />
    </div>
  );
};

export default ContractFinancialRecognition;
